import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import {
  Breadcrumbs,
  PageHeader,
  Button,
  CellText,
  Menu,
  Plus,
  DotsVertical,
  Edit01,
  Archive,
  BookOpen01,
} from "@opendate-app/setlist";
import { formatPrice } from "../../shared/teamCurrencyFormatting";
import ClassCategoryBadge from "../ClassesShared/ClassCategoryBadge";
import ClassFormatBadge from "../ClassesShared/ClassFormatBadge";
import EmptyStateCard from "../ClassesShared/EmptyStateCard";
import IndexToolbar, { IndexOverlays, IndexTable } from "../ClassesShared/IndexToolbar";
import useClassesIndexTable, { ClassesIndexTableContext, archivedStatusQuery } from "../ClassesShared/useClassesIndexTable";
import "../ClassesShared/classesShared.css";
import { templateLength } from "../ClassesShared/templateLength";
import { formatAdded } from "../ClassesShared/classLabels";
import { CLASS_TEMPLATE_FILTER_GROUPS } from "./constants/classTemplateFiltersConfig";
import { CLASS_TEMPLATE_COLUMNS, SORT_OPTIONS } from "./constants/classTemplateColumnsConfig";

function ClassTemplatesApp(props) {
  const { indexPath, classesPath, newPath, csrfToken, canManage, teamId, currencyCode, totalCount } = props;

  const table = useClassesIndexTable({
    indexPath,
    csrfToken,
    teamId,
    storageKey: "classTemplate",
    dataKey: "classTemplates",
    noun: "templates",
    kind: "template",
    columns: CLASS_TEMPLATE_COLUMNS,
    sortOptions: SORT_OPTIONS,
    filterGroups: CLASS_TEMPLATE_FILTER_GROUPS,
    buildQuery: (filters) => ({
      ...archivedStatusQuery(filters.status),
      ...(filters.format?.length ? { format_in: filters.format } : {}),
    }),
  });
  const { requestArchive } = table;

  const columns = useMemo(() => {
    const cols = table.visibleColumns.map((col) => {
      switch (col.id) {
        case "name":
          return {
            key: "name",
            header: "Template",
            primary: true,
            render: (t) => (
              <a href={`${indexPath}/${t.id}`} className="text-reset text-decoration-none">
                <CellText primary={t.name} supporting={templateLength(t.sessions_count, t.default_session_duration_minutes)} />
              </a>
            ),
          };
        case "category":
          return { key: "category", header: "Category", render: (t) => (t.category ? <ClassCategoryBadge category={t.category} /> : "—") };
        case "format":
          return { key: "format", header: "Format", render: (t) => <ClassFormatBadge format={t.format} /> };
        case "default_price":
          return {
            key: "default_price",
            header: "Default price",
            render: (t) => (t.default_price != null ? formatPrice(Number(t.default_price), currencyCode) : "—"),
          };
        case "default_capacity":
          return { key: "default_capacity", header: "Default capacity", render: (t) => t.default_capacity ?? "—" };
        case "classes_count":
          return { key: "classes_count", header: "Classes", render: (t) => t.classes_count ?? 0 };
        case "sessions_count":
          return { key: "sessions_count", header: "Sessions", render: (t) => t.sessions_count ?? 0 };
        case "internal_code":
          return { key: "internal_code", header: "Internal code", render: (t) => t.internal_code || "—" };
        case "created_at":
          return { key: "created_at", header: "Added", render: (t) => formatAdded(t.created_at) };
        default:
          return { key: col.id, header: col.label, render: () => "—" };
      }
    });

    if (canManage) {
      cols.push({
        key: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "right",
        render: (t) => (
          <Menu
            placement="bottom-end"
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${t.name}`} />}
            items={[
              { label: "Edit Template", icon: Edit01, onClick: () => { window.location.href = `${indexPath}/${t.id}/edit`; } },
              { label: t.archived ? "Unarchive Template" : "Archive Template", icon: Archive, onClick: () => requestArchive(t) },
            ]}
          />
        ),
      });
    }
    return cols;
  }, [table.visibleColumns, indexPath, canManage, requestArchive, currencyCode]);

  return (
    <ClassesIndexTableContext.Provider value={table}>
      <div className="od-themed p-4">
        <PageHeader
          breadcrumbs={<Breadcrumbs items={[{ label: "Classes", href: classesPath }, { label: "Templates" }]} />}
          title={<span role="heading" aria-level={1}>Templates</span>}
          supportingText="Reusable definitions you can schedule again and again."
          actions={
            canManage && (
              <Button as="a" variant="primary" icon={Plus} href={newPath}>
                New Template
              </Button>
            )
          }
        />

        {totalCount === 0 ? (
          <div className="mt-4">
            <EmptyStateCard
              icon={BookOpen01}
              title="Build your class catalog"
              description="Templates are reusable definitions of a class: name, description, format and default pricing. Create a template once, then schedule as many runs from it as you need."
              {...(canManage ? { primaryAction: { label: "Create Your First Template", icon: Plus, onClick: () => { window.location.href = newPath; } } } : {})}
            />
          </div>
        ) : (
          <>
            <IndexToolbar />
            <IndexTable columns={columns} empty="No templates yet." />
          </>
        )}

        <IndexOverlays />
        <ToastContainer icon={false} />
      </div>
    </ClassesIndexTableContext.Provider>
  );
}

ClassTemplatesApp.propTypes = {
  indexPath: PropTypes.string.isRequired,
  classesPath: PropTypes.string,
  newPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currencyCode: PropTypes.string,
  totalCount: PropTypes.number,
};

export default ClassTemplatesApp;
